import React from "react";
import { Link, Outlet } from "react-router-dom";
import { FileText, ShieldCheck, Calculator, ListChecks, Lock } from "lucide-react";

// Matches form720-ui's auth/AuthLayout.jsx brand mark, reusing this app's
// own logo mark/name instead of Form720's.
const BrandMark = ({ light = false }) => (
  <Link to="/" className="flex items-center gap-2.5 no-underline">
    <div className="w-9 h-9 rounded-[10px] bg-[var(--sign)] flex items-center justify-center flex-shrink-0">
      <FileText size={17} strokeWidth={2.2} className="text-white" />
    </div>
    <span className={`font-disp text-[18px] font-extrabold tracking-tight ${light ? "text-white" : "text-[var(--ink)]"}`}>
      EasyFile<span className={light ? "text-[var(--land-emerald)]" : "text-[var(--accent)]"}>2290</span>
    </span>
  </Link>
);

// Two-column split, ported from form720-ui's auth/AuthLayout.jsx: an info
// panel on the left (desktop only) and a plain white form panel on the
// right rendering the route's own page (Login.jsx) via Outlet - no logic
// here, purely the surrounding chrome. No top app-bar, matching Form720's
// auth screens (a slim mobile-only header replaces it).
//
// The left panel reuses the landing page's own --land-* gradient tokens
// (landing-aurora-deep, defined in index.css) instead of a hardcoded navy,
// so it re-colors with both the active app theme (day/night/dispatch) and
// stays visually consistent with Home.jsx's hero/CTA sections.
const AuthLayout = () => (
  <div className="app-shell min-h-screen grid md:grid-cols-2 font-body text-[15px] leading-[1.55] antialiased">
    {/* Left panel — hidden on mobile */}
    <div className="landing-aurora-deep hidden md:flex flex-col justify-between relative overflow-hidden p-12">
      <div className="absolute top-[-200px] right-[-200px] w-[600px] h-[600px] rounded-full pointer-events-none bg-[radial-gradient(circle,color-mix(in_srgb,var(--land-primary)_35%,transparent),transparent_70%)]" />
      <div className="absolute bottom-[-100px] left-[-100px] w-[400px] h-[400px] rounded-full pointer-events-none bg-[radial-gradient(circle,color-mix(in_srgb,var(--land-emerald)_25%,transparent),transparent_70%)]" />

      <div className="relative z-10">
        <BrandMark light />
      </div>

      <div className="relative z-10">
        <h2 className="font-disp text-[32px] font-extrabold text-white tracking-tight leading-tight mb-3.5">
          File Form 2290 in minutes
        </h2>
        <p className="text-[15px] text-white/65 leading-relaxed mb-9 max-w-[380px]">
          Get your stamped Schedule 1 back within seconds — trucks, businesses, and filings all in one place.
        </p>
        <div className="flex flex-col gap-3">
          {/* Mirrors the four points on form720-ui's auth panel. One wording
              change: 720's version says "IRS-published excise tax rates", which
              is its own form's language — Form 2290 is the Heavy Highway
              Vehicle Use Tax, so it cites the TY2026 rate tables instead. */}
          {[
            {
              icon: <Calculator size={20} className="text-white" />,
              title: "2026 rates built in",
              body: "Auto-calculated using the IRS-published TY2026 rate tables",
            },
            {
              icon: <ListChecks size={20} className="text-white" />,
              title: "File in one sitting",
              body: "Guided step by step, from business details to IRS submission",
            },
            {
              icon: <ShieldCheck size={20} className="text-white" />,
              title: "IRS-Authorized E-File Provider",
              body: "Direct submission to the IRS through an authorized e-file channel",
            },
            {
              icon: <Lock size={20} className="text-white" />,
              title: "Encrypted & secure",
              body: "256-bit SSL encryption in transit and at rest",
            },
          ].map(({ icon, title, body }) => (
            <div key={title} className="flex items-start gap-3.5 bg-white/[0.07] border border-white/10 rounded-[10px] p-4">
              <div className="w-10 h-10 rounded-[10px] bg-white/10 flex items-center justify-center flex-shrink-0">
                {icon}
              </div>
              <div>
                <div className="font-disp text-[15px] font-extrabold text-white leading-tight">{title}</div>
                <div className="text-xs text-white/55 mt-1 leading-relaxed">{body}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <p className="relative z-10 text-xs text-white/30">
        © 2026 Quantiva LLC · EasyFile2290 is a product of Quantiva LLC
      </p>
    </div>

    {/* Right panel */}
    <div className="flex flex-col bg-[var(--panel)] min-h-screen overflow-y-auto">
      <header className="md:hidden flex items-center px-5 py-4 border-b border-[var(--line)] sticky top-0 bg-[var(--panel)] z-10">
        <BrandMark />
      </header>

      <div className="flex-1 flex items-center justify-center px-6 py-10 md:px-10 md:py-12">
        <Outlet />
      </div>
    </div>
  </div>
);

export default AuthLayout;
